import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

function KPIPill({ label, value, color, small, alert }) {
  return (
    <div style={{
      background: alert ? 'rgba(239,68,68,0.08)' : 'var(--surface-2)',
      border: `1px solid ${alert ? 'rgba(239,68,68,0.4)' : 'var(--border)'}`,
      borderRadius: 8, padding: small ? '8px 12px' : '10px 14px',
      minWidth: small ? 90 : 110, flex: '1 1 0', textAlign: 'center',
    }}>
      <div style={{ fontSize: 10, color: alert ? '#ef4444' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: small ? 18 : 22, fontWeight: 700, color: color || 'var(--text)', fontFamily: 'DM Mono, monospace' }}>
        {value ?? '—'}
      </div>
    </div>
  );
}

function KPIGroup({ title, icon, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>
        {icon} {title}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  );
}

export default function PB101Overview() {
  const { get } = useApi();
  const { currentClub } = useClub();
  const [kpi, setKpi]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    get(`/pb101/overview?club=${currentClub.id}`)
      .then(setKpi)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentClub.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;

  if (error) return (
    <div style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>
      Failed to load overview — {error}
      <button onClick={load} style={{ marginLeft: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer' }}>
        Retry
      </button>
    </div>
  );

  if (!kpi) return null;

  const hasHotLeads = (kpi.hotLeadsForSales || 0) > 0;

  return (
    <div style={{ padding: '16px 0' }}>

      {/* ── HOT LEADS ALERT BANNER — only shown when hot leads exist ── */}
      {hasHotLeads && (
        <div style={{
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.4)',
          borderRadius: 8, padding: '10px 16px', marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>🔥</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: 13, color: '#ef4444', letterSpacing: '0.02em' }}>
                {kpi.hotLeadsForSales} Hot Lead{kpi.hotLeadsForSales !== 1 ? 's' : ''} Waiting for Sales
              </div>
              <div style={{ fontSize: 11, color: '#ef444488', marginTop: 1 }}>
                F.Desk flagged these — Sales must follow up within 4 hours
              </div>
            </div>
          </div>
          <span style={{ background: '#ef4444', color: '#fff', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 800 }}>
            URGENT
          </span>
        </div>
      )}

      {/* ── Two-column layout: KPI groups left, panels right ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24, alignItems: 'start' }}>

        {/* LEFT — KPI groups */}
        <div>

          {/* GAP-P15: Hot Leads for Sales KPI group */}
          {hasHotLeads && (
            <KPIGroup title="Sales Handoff" icon="🔥">
              <KPIPill
                label="Hot Leads for Sales"
                value={kpi.hotLeadsForSales}
                color="#ef4444"
                alert
              />
              <KPIPill
                label="Pending Follow-Up"
                value={kpi.pendingFollowUp}
                color={kpi.pendingFollowUp > 0 ? 'var(--yellow)' : undefined}
              />
            </KPIGroup>
          )}

          <KPIGroup title="PB101 Event" icon="🏓">
            <KPIPill label="Sessions Today"  value={kpi.sessionsToday}  color="var(--yellow)" />
            <KPIPill label="Attended Yes"    value={kpi.attendedYes}    color="var(--green)" />
            <KPIPill label="No Shows"        value={kpi.noShows}        color="var(--red)" />
            <KPIPill label="Active Records"  value={kpi.activeRecords} />
          </KPIGroup>

          <KPIGroup title="Day-of Trial Ask" icon="🎯">
            <KPIPill label="Trials Sold"      value={kpi.trialsSold}      color="var(--green)" small />
            <KPIPill label="Memberships Sold" value={kpi.membershipsSold} color="var(--green)" small />
            <KPIPill label="Failed to Ask"    value={kpi.failedToAsk}     color={kpi.failedToAsk > 0 ? 'var(--red)' : undefined} small />
            <KPIPill label="Trial Conv. %"    value={`${kpi.trialConvRate}%`} small />
          </KPIGroup>

          <KPIGroup title="Follow-Up" icon="📞">
            <KPIPill label="F/U Sent"       value={kpi.followupSent}    small />
            <KPIPill label="Re-Offers"      value={kpi.reOffers}        small />
            <KPIPill label="Pending Follow" value={kpi.pendingFollowUp} color={kpi.pendingFollowUp > 0 ? 'var(--yellow)' : undefined} small />
            <KPIPill label="Lost"           value={kpi.lost}            color="var(--text-muted)" small />
          </KPIGroup>

          {/* GAP-P10: Membership Conversion prominent */}
          <KPIGroup title="Conversion" icon="📊">
            {/* Large prominent conversion display */}
            <div style={{
              flex: '1 1 100%',
              background: kpi.converted > 0 ? 'rgba(34,197,94,0.08)' : 'var(--surface-2)',
              border: `1px solid ${kpi.converted > 0 ? 'rgba(34,197,94,0.35)' : 'var(--border)'}`,
              borderRadius: 8, padding: '12px 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                  Membership Conversions
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: kpi.converted > 0 ? 'var(--green)' : 'var(--text)', fontFamily: 'DM Mono, monospace', lineHeight: 1 }}>
                  {kpi.converted}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  {kpi.trialsSold + kpi.membershipsSold} trial/membership asks made · {kpi.pb101ToMemberRate}% PB101→Member rate
                </div>
              </div>
              {kpi.converted > 0 && (
                <div style={{ fontSize: 36 }}>✅</div>
              )}
            </div>
            <KPIPill label="Member Conv. %"  value={`${kpi.memberConvRate}%`} />
            <KPIPill label="PB101→Member %"  value={`${kpi.pb101ToMemberRate}%`} color="var(--yellow)" />
            <KPIPill label="Total Records"   value={kpi.totalRecords} />
          </KPIGroup>
        </div>

        {/* RIGHT — panels */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* GAP-P15: HOT LEADS FOR SALES panel */}
          {hasHotLeads && (
            <div style={{
              background: 'var(--surface-2)',
              border: '1px solid rgba(239,68,68,0.35)',
              borderRadius: 10, overflow: 'hidden',
            }}>
              <div style={{
                background: 'rgba(239,68,68,0.12)', borderBottom: '1px solid rgba(239,68,68,0.25)',
                padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#ef4444' }}>
                  🔥 Hot Leads for Sales
                </span>
                <span style={{ background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
                  {(kpi.hotLeads || []).length}
                </span>
              </div>
              <div style={{ padding: '6px 8px', fontSize: 10, color: '#ef444488', fontStyle: 'italic', borderBottom: '1px solid rgba(239,68,68,0.1)' }}>
                F.Desk flagged · Sales must contact within 4 hours
              </div>
              <div style={{ padding: '10px 12px', maxHeight: 240, overflowY: 'auto' }}>
                {(kpi.hotLeads || []).map(r => (
                  <div key={r.id} style={{
                    background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
                    borderRadius: 7, padding: '8px 10px', marginBottom: 6,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                      <div style={{ fontWeight: 700, fontSize: 12, color: '#e5e5e5' }}>{r.name}</div>
                      <span style={{ background: '#ef444422', color: '#ef4444', border: '1px solid #ef444440', fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3 }}>
                        FOLLOW UP NOW
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 10, fontSize: 10, color: '#666' }}>
                      {r.sessionDate && <span>📅 {r.sessionDate}</span>}
                      {r.offeredBy   && <span>by {r.offeredBy}</span>}
                      {r.followupTiming && <span>⏱ {r.followupTiming}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* NEEDS FOLLOW-UP panel */}
          <div style={{
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            borderRadius: 10, overflow: 'hidden',
          }}>
            <div style={{
              background: 'rgba(234,179,8,0.12)', borderBottom: '1px solid var(--border)',
              padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--yellow)' }}>
                📞 Needs Follow-Up
              </span>
              <span style={{ background: 'rgba(234,179,8,0.3)', color: 'var(--yellow)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
                {(kpi.needsFollowUp || []).length}
              </span>
            </div>
            <div style={{ padding: '10px 12px', maxHeight: 280, overflowY: 'auto' }}>
              {(kpi.needsFollowUp || []).length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>
                  All caught up ✓
                </div>
              ) : (
                kpi.needsFollowUp.map(r => (
                  <div key={r.id} style={{
                    background: 'var(--surface-3)', borderRadius: 7, padding: '8px 10px', marginBottom: 6,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.sessionDate}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                      {r.askResult && (
                        <span style={{ background: 'rgba(234,88,12,0.2)', color: '#f97316', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
                          {r.askResult}
                        </span>
                      )}
                      <span style={{ background: 'var(--yellow)', color: '#000', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
                        RE-OFFER
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* RECENT CONVERSIONS panel */}
          <div style={{
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            borderRadius: 10, overflow: 'hidden',
          }}>
            <div style={{
              background: 'rgba(34,197,94,0.1)', borderBottom: '1px solid var(--border)',
              padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--green)' }}>
                ✅ Recent Conversions
              </span>
              <span style={{ background: 'rgba(34,197,94,0.2)', color: 'var(--green)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
                {(kpi.recentConversions || []).length}
              </span>
            </div>
            <div style={{ padding: '10px 12px', maxHeight: 280, overflowY: 'auto' }}>
              {(kpi.recentConversions || []).length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>
                  No conversions this week
                </div>
              ) : (
                kpi.recentConversions.map(r => (
                  <div key={r.id} style={{
                    background: 'var(--surface-3)', borderRadius: 7, padding: '8px 10px', marginBottom: 6,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.date}</div>
                    </div>
                    <span style={{
                      background: r.type === 'MBR. SOLD' ? 'rgba(34,197,94,0.2)' : 'rgba(59,130,246,0.2)',
                      color: r.type === 'MBR. SOLD' ? 'var(--green)' : '#60a5fa',
                      fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                    }}>
                      {r.type}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
